import useCustomToast from 'hooks/toast';
import useComprehensiveTopNav from 'hooks/useComprehensiveTopNav';
import Api from 'lib/api';
import { Button, LoadingIndicator, Typography } from 'lib/components';
import { TextField } from 'lib/components/FormFields';
import React, { useEffect, useState } from 'react';
import { useSelectAuthorization } from 'store';
import { DoctorMatch, Hospital } from 'types';

const emptyForm = {
	lastName: '',
	firstName: '',
	phoneNumber: '',
	hospitalIds: [] as string[],
};

const DoctorsScreen: React.FC = () => {
	const toast = useCustomToast();
	const { canUpdateHospital } = useSelectAuthorization();
	const [doctors, setDoctors] = useState<DoctorMatch[]>([]);
	const [hospitals, setHospitals] = useState<Hospital[]>([]);
	const [loading, setLoading] = useState(true);
	const [form, setForm] = useState(emptyForm);
	const [editing, setEditing] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [matches, setMatches] = useState<DoctorMatch[] | null>(null);

	const load = async () => {
		const [doctorResult, hospitalResult] = await Promise.all([Api.doctors.getAll(), Api.hospitals.getAll()]);
		if (doctorResult.data) setDoctors(doctorResult.data);
		if (hospitalResult.data) setHospitals(hospitalResult.data);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, []);

	useComprehensiveTopNav('Doctors');

	const toggleHospital = (id: string) => {
		setForm((current) => ({
			...current,
			hospitalIds: current.hospitalIds.includes(id)
				? current.hospitalIds.filter((item) => item !== id)
				: [...current.hospitalIds, id],
		}));
	};

	const submit = async (extra: Record<string, unknown> = {}) => {
		if (saving) return;
		if (!form.lastName.trim() || !form.firstName.trim() || !form.phoneNumber.trim() || form.hospitalIds.length === 0) {
			toast('Last name, first name, phone, and at least one hospital are required.', 'error');
			return;
		}
		setSaving(true);
		const input = { ...form, ...extra };
		const result = editing ? await Api.doctors.update(editing, input) : await Api.doctors.create(input);
		setSaving(false);
		if (result.error?.body?.code === 'confirm_doctor_match') {
			setMatches(result.error.body.doctors || []);
			return;
		}
		if (result.error) {
			toast(result.error.message, 'error');
			return;
		}
		setMatches(null);
		setForm(emptyForm);
		setEditing(null);
		toast(editing ? 'Physician updated' : 'Physician saved');
		load();
	};

	const remove = async (doctor: DoctorMatch) => {
		if (doctor.jobCount > 0) {
			toast('This physician has jobs and cannot be deleted.', 'error');
			return;
		}
		if (!window.confirm(`Delete ${doctor.displayName}?`)) return;
		const result = await Api.doctors.delete(doctor.id);
		if (result.error) {
			toast(result.error.message, 'error');
			return;
		}
		load();
	};

	if (loading) return <LoadingIndicator />;

	return (
		<div style={{ padding: '1.5rem' }}>
			{canUpdateHospital && (
				<div style={{ background: '#fff', padding: '1rem', marginBottom: '1.5rem' }}>
					<Typography variant="h5" weight="bold">
						{editing ? 'Edit physician' : 'Add physician'}
					</Typography>
					<div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
						<TextField
							label="Last name"
							variant="labelled"
							value={form.lastName}
							onChange={(event) => setForm({ ...form, lastName: event.target.value })}
						/>
						<TextField
							label="First name"
							variant="labelled"
							value={form.firstName}
							onChange={(event) => setForm({ ...form, firstName: event.target.value })}
						/>
						<TextField
							label="Phone"
							variant="labelled"
							value={form.phoneNumber}
							onChange={(event) => setForm({ ...form, phoneNumber: event.target.value })}
						/>
					</div>
					<div style={{ marginTop: '1rem' }}>
						<Typography>Hospitals</Typography>
						{hospitals.map((hospital) => (
							<label key={hospital.id} style={{ display: 'block', marginTop: '0.35rem' }}>
								<input
									type="checkbox"
									checked={form.hospitalIds.includes(String(hospital.id))}
									onChange={() => toggleHospital(String(hospital.id))}
								/>{' '}
								{hospital.name}
							</label>
						))}
					</div>
					<div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
						<Button title={saving ? 'Saving...' : 'Save'} disabled={saving} onClick={() => submit()} />
						{editing && (
							<Button
								title="Cancel"
								variant="clear"
								onClick={() => {
									setEditing(null);
									setForm(emptyForm);
									setMatches(null);
								}}
							/>
						)}
					</div>
					{matches && matches.length > 0 && (
						<div style={{ marginTop: '1rem', borderTop: '1px solid #ddd', paddingTop: '1rem' }}>
							<Typography weight="bold">A physician with this name is already on file.</Typography>
							{matches.map((doctor) => (
								<div key={doctor.id} style={{ marginTop: '0.75rem' }}>
									<Typography>
										{doctor.displayName} · {doctor.phoneNumber} ·{' '}
										{doctor.hospitals.map((hospital) => hospital.name).join(', ') || 'No hospitals'}
									</Typography>
									<Button
										title="Add these hospitals to this physician"
										onClick={() =>
											submit({ confirmDoctorAction: 'attach', attachToDoctorId: doctor.id })
										}
									/>
								</div>
							))}
							<div style={{ marginTop: '0.75rem' }}>
								<Button title="Create another person" onClick={() => submit({ confirmDoctorAction: 'create' })} />
							</div>
						</div>
					)}
				</div>
			)}
			{doctors.map((doctor) => (
				<div
					key={doctor.id}
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						gap: '1rem',
						padding: '0.75rem 0',
						borderBottom: '1px solid #eee',
					}}
				>
					<div>
						<Typography weight="bold">{doctor.displayName}</Typography>
						<Typography>
							{doctor.phoneNumber} · {doctor.hospitals.map((hospital) => hospital.name).join(', ')} ·{' '}
							{doctor.jobCount} jobs
						</Typography>
					</div>
					{canUpdateHospital && (
						<div style={{ display: 'flex', gap: '0.5rem' }}>
							<Button
								title="Edit"
								variant="clear"
								onClick={() => {
									setEditing(doctor.id);
									setMatches(null);
									setForm({
										lastName: doctor.lastName,
										firstName: doctor.firstName,
										phoneNumber: doctor.phoneNumber,
										hospitalIds: doctor.hospitalIds,
									});
								}}
							/>
							{doctor.jobCount === 0 && (
								<Button title="Delete" variant="clear" onClick={() => remove(doctor)} />
							)}
						</div>
					)}
				</div>
			))}
		</div>
	);
};

export default DoctorsScreen;
